import { useEffect, useRef } from 'react';
import { Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import BookingCard from './BookingCard';
import BookingCardSkeleton from './BookingCardSkeleton';
import BookingEmptyState from './BookingEmptyState';
import type { Booking, RangeKey, StatusFilterKey } from '../../../types/provider';
import { todayInTashkent, addDaysToDateStr } from '../../../utils/dateTz';

const { Text } = Typography;

function groupByDate(bookings: Booking[]): { date: string; items: Booking[] }[] {
  const map = new Map<string, Booking[]>();
  for (const b of bookings) {
    const date = b.startTime.slice(0, 10);
    const group = map.get(date) ?? [];
    group.push(b);
    map.set(date, group);
  }
  return Array.from(map.entries()).map(([date, items]) => ({
    date,
    items: items.sort((a, b) => a.startTime.localeCompare(b.startTime)),
  }));
}

function sortGroupsForRange(
  groups: { date: string; items: Booking[] }[],
  range: RangeKey,
): { date: string; items: Booking[] }[] {
  const sorted = [...groups].sort((a, b) => a.date.localeCompare(b.date));
  if (range === 'today') return sorted;
  const today = todayInTashkent();
  const present = sorted.filter((g) => g.date >= today);
  const past = sorted.filter((g) => g.date < today).reverse();
  return [...present, ...past];
}

interface Props {
  bookings: Booking[];
  range: RangeKey;
  statusFilter: StatusFilterKey;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpenDetail: (booking: Booking) => void;
}

export default function BookingList({
  bookings,
  range,
  statusFilter,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  onOpenDetail,
}: Props) {
  const { t } = useTranslation('provider');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const formatDateHeader = (dateStr: string): { primary: string; secondary: string; isToday: boolean; isPast: boolean } => {
    const today = todayInTashkent();
    const tomorrow = addDaysToDateStr(today, 1);
    const d = new Date(dateStr + 'T00:00:00');
    const monthLabel = t(`bookings.dateHeader.months.${d.getMonth()}`);
    const dayLabel = `${d.getDate()} ${monthLabel}`;
    if (dateStr === today) {
      return { primary: t('bookings.dateHeader.today'), secondary: dayLabel, isToday: true, isPast: false };
    }
    if (dateStr === tomorrow) {
      return { primary: t('bookings.dateHeader.tomorrow'), secondary: dayLabel, isToday: false, isPast: false };
    }
    const weekdayLabel = t(`bookings.dateHeader.weekdays.${d.getDay()}`);
    return {
      primary: weekdayLabel,
      secondary: dayLabel,
      isToday: false,
      isPast: dateStr < today,
    };
  };

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore]);

  if (isLoading) {
    return (
      <div key="loading" className="bl-animate-in">
        <BookingCardSkeleton />
        <BookingCardSkeleton />
        <BookingCardSkeleton />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div key="empty" className="bl-animate-in">
        <BookingEmptyState range={range} statusFilter={statusFilter} />
      </div>
    );
  }

  const groups = sortGroupsForRange(groupByDate(bookings), range);

  return (
    <div key="ready" className="bl-animate-in">
      {groups.map(({ date, items }, groupIdx) => {
        const header = formatDateHeader(date);
        return (
          <div key={date} style={{ marginBottom: groupIdx === groups.length - 1 ? 0 : 6 }}>
            {/* Sticky date header */}
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 5,
                padding: '8px 16px 6px',
                background:
                  'linear-gradient(to bottom, var(--bl-surface-soft, #f7f8fa) 0%, var(--bl-surface-soft, #f7f8fa) 70%, rgba(247,248,250,0) 100%)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: header.isToday
                      ? 'var(--bl-accent, #3390ec)'
                      : header.isPast
                        ? 'var(--bl-text-muted, #9ca3af)'
                        : 'var(--bl-text-strong, #0f172a)',
                    letterSpacing: -0.1,
                    textTransform: 'uppercase',
                  }}
                >
                  {header.primary}
                </Text>
                <Text
                  style={{
                    fontSize: 11,
                    color: 'var(--bl-text-muted, #9ca3af)',
                    fontWeight: 500,
                  }}
                >
                  · {header.secondary}
                </Text>
                <div style={{ flex: 1 }} />
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--bl-text-muted, #9ca3af)',
                    background: 'var(--bl-surface, #fff)',
                    border: '1px solid var(--bl-divider, #ebebeb)',
                    borderRadius: 10,
                    padding: '1px 7px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {items.length}
                </span>
              </div>
            </div>

            {items.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        );
      })}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {isLoadingMore && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      )}

      {!hasMore && bookings.length > 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '14px 16px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <div
            style={{
              height: 1,
              flex: 1,
              maxWidth: 40,
              background: 'var(--bl-divider, #e5e7eb)',
            }}
          />
          <Text
            type="secondary"
            style={{ fontSize: 11, color: 'var(--bl-text-muted, #9ca3af)' }}
          >
            {t('bookings.loadMore.allLoaded')}
          </Text>
          <div
            style={{
              height: 1,
              flex: 1,
              maxWidth: 40,
              background: 'var(--bl-divider, #e5e7eb)',
            }}
          />
        </div>
      )}
    </div>
  );
}
